import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { edgeErrorMessage } from "@/lib/edge-errors";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { DBCrmMeeting, MeetingLocationType } from "@/lib/db-types";
import type { FreeBusyHost } from "@/lib/meeting-slots";

export interface CustomerMeeting extends DBCrmMeeting {
  host: { full_name: string; avatar_url: string | null } | null;
  contact: { full_name: string } | null;
  summary: { id: string; status: string }[];
}

const SELECT_WITH_REFS =
  "*, host:users!crm_meetings_host_user_id_fkey(full_name, avatar_url), contact:customer_contacts!crm_meetings_contact_id_fkey(full_name), summary:meeting_summaries(id, status)";

/**
 * Kundekortets møter, kommende og avholdte. Bundet av customer_id. Tabellen heter
 * crm_meetings fordi public.meetings er prosjektmøter (oppstart/debrief).
 */
export function useCustomerMeetings(customerId: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["customer-meetings", customerId],
    queryFn: async (): Promise<CustomerMeeting[]> => {
      const { data, error } = await supabase
        .from("crm_meetings")
        .select(SELECT_WITH_REFS)
        .eq("customer_id", customerId!)
        .order("start_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as CustomerMeeting[];
    },
    enabled: !!customerId && (options.enabled ?? true),
    staleTime: 60 * 1000,
  });
}

/**
 * Ledighet for vertene (edge function calendar-freebusy). Maks 14 dager. Feil per vert
 * kommer som `error` på verten og kaster ikke; en feil for hele kallet kaster.
 */
export function useFreeBusy(range: { start: string; end: string } | null, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["freebusy", range?.start, range?.end],
    queryFn: async (): Promise<FreeBusyHost[]> => {
      const { data, error } = await supabase.functions.invoke<{ hosts?: FreeBusyHost[]; error?: string; skipped?: string }>(
        "calendar-freebusy",
        { body: { start: range!.start, end: range!.end } },
      );
      if (error) throw new Error(await edgeErrorMessage(error, data, "Kunne ikke hente ledighet"));
      if (data?.skipped === "flag_off") throw new Error("Booking er ikke skrudd på ennå");
      if (data?.error) throw new Error(data.error);
      return data?.hosts ?? [];
    },
    enabled: !!range && (options.enabled ?? true),
    staleTime: 60 * 1000,
  });
}

export interface BookMeetingInput {
  hostUserId: string;
  contactId: string;
  customerId?: string | null;
  leadId?: string | null;
  opportunityId?: string | null;
  start: string;
  end: string;
  title: string;
  message?: string | null;
  locationType: MeetingLocationType;
  address?: string | null;
  internalNote?: string | null;
}

export interface BookMeetingResult {
  meeting_id: string;
  html_link: string | null;
  meet_uri: string | null;
  warnings: ("no_mobile" | "no_email" | "transcription_config_failed" | "no_meet_link")[];
}

export const BOOKING_WARNING_TEXT: Record<BookMeetingResult["warnings"][number], string> = {
  no_mobile: "Kontakten har ikke mobilnummer, så SMS-påminnelsene hoppes over.",
  no_email: "Kontakten har ikke e-post.",
  transcription_config_failed: "Transkripsjon ble ikke slått på automatisk. Verten må slå den på i møtet.",
  no_meet_link: "Google ga ikke noen Meet-lenke til møtet.",
};

/** Booker møtet via edge function calendar-book-meeting. Bookeren er alltid den innloggede brukeren. */
export function useBookMeeting() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { activeUser, isImpersonating } = useImpersonation();
  return useMutation<BookMeetingResult, Error, BookMeetingInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke<BookMeetingResult & { error?: string; skipped?: string }>(
        "calendar-book-meeting",
        {
          body: {
            host_user_id: input.hostUserId,
            contact_id: input.contactId,
            customer_id: input.customerId ?? undefined,
            lead_id: input.leadId ?? undefined,
            opportunity_id: input.opportunityId ?? undefined,
            start: input.start,
            end: input.end,
            title: input.title,
            message: input.message ?? undefined,
            location_type: input.locationType,
            address: input.address ?? undefined,
            internal_note: input.internalNote ?? undefined,
            // Bookeren er alltid den ekte brukeren; den impersonerte føres bare i loggen.
            on_behalf_of: isImpersonating && activeUser ? activeUser.id : undefined,
          },
        },
      );
      if (error) throw new Error(await edgeErrorMessage(error, data, "Kunne ikke booke møtet"));
      if (data?.skipped === "flag_off") throw new Error("Booking er ikke skrudd på ennå");
      if (data?.error) throw new Error(data.error);
      if (!data?.meeting_id) throw new Error("Uventet svar fra bookingtjenesten");
      return data;
    },
    onSuccess: async (result, input) => {
      await invalidateFor(qc, "crm_meetings", { customerId: input.customerId ?? undefined });
      await invalidateFor(qc, "leads", { userId: user?.id });
      await invalidateFor(qc, "lead_attempts", { leadId: input.leadId ?? undefined });
      await invalidateFor(qc, "opportunities");
      await invalidateFor(qc, "customer_activities", { customerId: input.customerId ?? undefined });
      for (const w of result.warnings) toast.warning(BOOKING_WARNING_TEXT[w]);
    },
    onError: (err) => toast.error(err.message),
  });
}
